import { useEffect, useRef } from 'react';

// ==============================|| BUILD VERSION CHECK ||============================== //
// index.html can end up cached (browser or an intermediate proxy) after a new deploy,
// leaving a tab stuck running an old build indefinitely. This compares the running
// build's id against dist/version.json (always fetched with cache: 'no-store', so it's
// never stale) and, on a mismatch, reloads via a cache-busted URL — a plain
// location.reload() isn't enough, since it can still be served from the same cache.
//
// Does not fix the root cause (index.html should be served with Cache-Control: no-cache
// at the server) — it's a safety net so nobody stays silently stuck on old code.

const VERSION_URL = `${import.meta.env.BASE_URL}version.json`;
const CHECK_INTERVAL_MS = 20 * 60 * 1000; // 20 minutes
const CACHE_BUST_PARAM = '_v';

function stripCacheBustParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) return;
  url.searchParams.delete(CACHE_BUST_PARAM);
  window.history.replaceState(null, '', url.toString());
}

function reloadWithCacheBust() {
  const url = new URL(window.location.href);
  url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

export function useBuildVersionCheck() {
  const checkingRef = useRef(false);
  const reloadedRef = useRef(false);

  useEffect(() => {
    stripCacheBustParam();

    const checkVersion = async () => {
      if (checkingRef.current || reloadedRef.current) return;
      checkingRef.current = true;
      try {
        const res = await fetch(VERSION_URL, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (data.buildId && data.buildId !== __APP_BUILD_ID__) {
          reloadedRef.current = true;
          reloadWithCacheBust();
        }
      } catch {
        // Offline or version.json unreachable — try again next cycle.
      } finally {
        checkingRef.current = false;
      }
    };

    const initialTimeout = setTimeout(checkVersion, 10_000);
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);
}
